import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Loader2, PackagePlus, Search, RefreshCw, Link2, AlertTriangle } from 'lucide-react';

const ACTIONS = [
  { value: 'receive', label: 'Receive stock (from supplier)', hint: 'Adds units to IN_STOCK from NONE.' },
  { value: 'set', label: 'Set count (stock take)', hint: 'Overwrites current count via PHYSICAL_COUNT.' },
  { value: 'waste', label: 'Waste / write-off', hint: 'Moves units from IN_STOCK to WASTE.' },
];

export default function InventoryPage() {
  const { api, currentLocation } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [connectionStatus, setConnectionStatus] = useState(null);

  const [supportedActions, setSupportedActions] = useState([]);

  // Adjust dialog state
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustRow, setAdjustRow] = useState(null);
  const [action, setAction] = useState('receive');
  const [quantity, setQuantity] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Link ingredient dialog state
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkRow, setLinkRow] = useState(null);
  const [ingredients, setIngredients] = useState([]);
  const [selectedIngredientId, setSelectedIngredientId] = useState('');
  const [linkSubmitting, setLinkSubmitting] = useState(false);

  const load = useCallback(async () => {
    if (!currentLocation) return;
    setLoading(true);
    try {
      const [statusRes, invRes] = await Promise.all([
        api.get('/square/status', { params: { location_id: currentLocation.id } }),
        api.get('/inventory', { params: { location_id: currentLocation.id } }).catch((err) => {
          if (err?.response?.status === 400 || err?.response?.status === 403 || err?.response?.status === 501) {
            return { data: { items: [], note: err.response.data?.detail, supported_actions: [] } };
          }
          throw err;
        }),
      ]);
      setConnectionStatus(statusRes.data);
      setItems(invRes.data?.items || []);
      setSupportedActions(invRes.data?.supported_actions || []);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Failed to load inventory');
    } finally {
      setLoading(false);
    }
  }, [api, currentLocation]);

  useEffect(() => { load(); }, [load]);

  const filtered = search
    ? items.filter((i) =>
        (i.name || '').toLowerCase().includes(search.toLowerCase()) ||
        (i.variation_name || '').toLowerCase().includes(search.toLowerCase()))
    : items;

  const openAdjust = (row) => {
    setAdjustRow(row);
    setAction('receive');
    setQuantity('');
    setNote('');
    setAdjustOpen(true);
  };

  const submitAdjust = async () => {
    const q = parseFloat(quantity);
    if (!q || q <= 0) return toast.error('Enter a quantity greater than 0');
    setSubmitting(true);
    try {
      const res = await api.post('/inventory/adjust', {
        location_id: currentLocation.id,
        variation_external_id: adjustRow.variation_external_id,
        action,
        quantity: q,
        note: note || null,
      });
      const newQty = res.data?.in_stock;
      toast.success(
        `Stock updated${newQty !== null && newQty !== undefined ? ` — now ${newQty} in stock` : ''}`
      );
      setAdjustOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Adjustment failed');
    } finally {
      setSubmitting(false);
    }
  };

  const openLink = async (row) => {
    setLinkRow(row);
    setSelectedIngredientId(row.ingredient?.id || '');
    try {
      const res = await api.get('/ingredients', { params: { location_id: currentLocation.id } });
      setIngredients(res.data || []);
    } catch (err) {
      toast.error('Failed to load ingredients');
    }
    setLinkOpen(true);
  };

  const submitLink = async () => {
    setLinkSubmitting(true);
    try {
      await api.post('/inventory/link-ingredient', {
        location_id: currentLocation.id,
        variation_external_id: linkRow.variation_external_id,
        ingredient_id: selectedIngredientId || null,
      });
      toast.success(selectedIngredientId ? 'Ingredient linked' : 'Ingredient unlinked');
      setLinkOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Link failed');
    } finally {
      setLinkSubmitting(false);
    }
  };

  const needsReconnect = connectionStatus?.connected && connectionStatus?.has_inventory_write === false;
  const notConnected = !connectionStatus?.connected;

  return (
    <div className="p-6 space-y-6" data-testid="inventory-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Inventory</h1>
          <p className="text-muted-foreground">
            Live stock counts from Square. Receive stock, adjust wastage, or link items to internal ingredients.
          </p>
        </div>
        <Button onClick={load} variant="outline" disabled={loading} data-testid="inventory-refresh-btn">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {notConnected && (
        <Card className="border-amber-200 bg-amber-50" data-testid="inventory-not-connected">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium">Square is not connected for this location.</p>
              <p className="text-sm text-muted-foreground">Head to Settings and connect Square to enable inventory management.</p>
            </div>
            <Button variant="outline" onClick={() => navigate('/settings')} data-testid="inventory-goto-settings-btn">Open Settings</Button>
          </CardContent>
        </Card>
      )}

      {needsReconnect && (
        <Card className="border-amber-200 bg-amber-50" data-testid="inventory-reconnect-required">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium">Reconnect Square to enable stock management.</p>
              <p className="text-sm text-muted-foreground">Your existing connection is missing the <code className="font-mono">INVENTORY_READ</code> and <code className="font-mono">INVENTORY_WRITE</code> scopes. Reconnect once from Settings — no historical data will be lost.</p>
              {connectionStatus?.missing_scopes?.length > 0 && (
                <p className="text-xs text-muted-foreground mt-1">Missing: {connectionStatus.missing_scopes.join(', ')}</p>
              )}
            </div>
            <Button onClick={() => navigate('/settings')} data-testid="inventory-reconnect-btn">Reconnect Square</Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Catalog stock</CardTitle>
            <CardDescription>
              {loading ? 'Loading…' : `${filtered.length} of ${items.length} items`}
            </CardDescription>
          </div>
          <div className="relative w-64">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search item…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="inventory-search-input"
            />
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-10 flex justify-center text-muted-foreground">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground">
              {notConnected ? 'Connect Square to see live stock counts.' : 'No catalog items yet — run Sync Now from Settings.'}
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead>Variation</TableHead>
                    <TableHead className="text-right">Price</TableHead>
                    <TableHead className="text-right">In stock</TableHead>
                    <TableHead>Linked ingredient</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((row) => (
                    <TableRow key={row.variation_external_id} data-testid={`inventory-row-${row.variation_external_id}`}>
                      <TableCell className="font-medium">{row.name || '—'}</TableCell>
                      <TableCell className="text-muted-foreground">{row.variation_name || '—'}</TableCell>
                      <TableCell className="text-right mono-num">{row.price != null ? `£${row.price.toFixed(2)}` : '—'}</TableCell>
                      <TableCell className="text-right mono-num font-semibold" data-testid={`inventory-stock-${row.variation_external_id}`}>
                        {row.in_stock ?? 0}
                      </TableCell>
                      <TableCell>
                        {row.ingredient ? (
                          <button
                            type="button"
                            onClick={() => navigate('/ingredients')}
                            className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                            data-testid={`inventory-linked-ing-${row.variation_external_id}`}
                          >
                            <Link2 className="w-3 h-3" /> {row.ingredient.name}
                          </button>
                        ) : (
                          <Badge variant="outline" className="text-[10px]">Unlinked</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right space-x-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openLink(row)}
                          data-testid={`inventory-link-btn-${row.variation_external_id}`}
                        >
                          <Link2 className="w-3 h-3 mr-1" /> Link
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => openAdjust(row)}
                          disabled={supportedActions.length === 0 || !connectionStatus?.has_inventory_write}
                          data-testid={`inventory-adjust-btn-${row.variation_external_id}`}
                        >
                          <PackagePlus className="w-3 h-3 mr-1" /> Adjust
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Adjust dialog */}
      <Dialog open={adjustOpen} onOpenChange={setAdjustOpen}>
        <DialogContent data-testid="inventory-adjust-dialog">
          <DialogHeader>
            <DialogTitle>Adjust stock — {adjustRow?.name}</DialogTitle>
            <DialogDescription>{adjustRow?.variation_name || 'default variation'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Action</Label>
              <Select value={action} onValueChange={setAction}>
                <SelectTrigger data-testid="inventory-action-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ACTIONS.map((a) => (
                    <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">{ACTIONS.find((a) => a.value === action)?.hint}</p>
            </div>
            <div>
              <Label>Quantity</Label>
              <Input
                type="number"
                min="0"
                step="any"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                data-testid="inventory-qty-input"
              />
            </div>
            <div>
              <Label>Note (optional)</Label>
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Supplier ref, waste reason…" data-testid="inventory-note-input" />
            </div>
            <div className="text-sm text-muted-foreground">
              Current in stock: <span className="font-medium">{adjustRow?.in_stock ?? 0}</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjustOpen(false)} disabled={submitting}>Cancel</Button>
            <Button onClick={submitAdjust} disabled={submitting} data-testid="inventory-adjust-submit-btn">
              {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link ingredient dialog */}
      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent data-testid="inventory-link-dialog">
          <DialogHeader>
            <DialogTitle>Link to internal ingredient</DialogTitle>
            <DialogDescription>Jump straight to recipe & cost data from the inventory list.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Square item</Label>
              <p className="text-sm">{linkRow?.name} {linkRow?.variation_name ? `— ${linkRow.variation_name}` : ''}</p>
            </div>
            <div>
              <Label>Ingredient</Label>
              <Select value={selectedIngredientId || 'none'} onValueChange={(v) => setSelectedIngredientId(v === 'none' ? '' : v)}>
                <SelectTrigger data-testid="inventory-link-select"><SelectValue placeholder="Choose ingredient" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Unlink —</SelectItem>
                  {ingredients.map((ing) => (
                    <SelectItem key={ing.id} value={ing.id}>{ing.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkOpen(false)} disabled={linkSubmitting}>Cancel</Button>
            <Button onClick={submitLink} disabled={linkSubmitting} data-testid="inventory-link-submit-btn">
              {linkSubmitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              Save link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
